import React, { useState } from 'react';
import { Bell, Check, X, Volume2, VolumeX } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { formatIST } from '../utils/timezone';

const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    triggerSound,
    soundEnabled,
    enableSound 
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_approved':
      case 'payment_approved':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'order_rejected':
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return <Bell className="w-4 h-4 text-primary" />;
    }
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        onClick={handleBellClick}
        className={`relative p-2 rounded-full hover:bg-white/10 transition-all ${
          unreadCount > 0 ? 'animate-pulse' : ''
        }`}
        data-testid="notification-bell"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'text-primary' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto glass-card z-50 shadow-xl" data-testid="notification-dropdown">
            <div className="p-4 border-b border-white/10">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-primary hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              
              {/* Sound toggle */}
              <button
                onClick={soundEnabled ? triggerSound : enableSound}
                className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                  soundEnabled 
                    ? 'bg-green-500/20 text-green-500' 
                    : 'bg-yellow-500/20 text-yellow-500'
                }`}
              >
                {soundEnabled ? (
                  <>
                    <Volume2 className="w-3 h-3" />
                    Sound ON - Click to test
                  </>
                ) : (
                  <>
                    <VolumeX className="w-3 h-3" />
                    Sound OFF - Click to enable
                  </>
                )}
              </button>
            </div>
            
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No notifications
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {notifications.slice(0, 10).map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-3 hover:bg-white/5 cursor-pointer transition-all ${
                      !notif.read ? 'bg-primary/10 border-l-2 border-primary' : ''
                    }`}
                    onClick={() => markAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className="mt-1">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.read ? 'font-semibold' : ''}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {notif.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatIST(notif.created_at)} IST
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-primary mt-2 animate-pulse" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
